import { describeClipboardError } from '@/helpers/copyHelpers/describeClipboardError';
import { type NoteEditorType } from '@/models/analytics/features/notes';
import { RtfClipboardError } from '@/tauri/controller/clipboard';
import { datadogRum } from '@datadog/browser-rum';
import { getCurrentSessionId } from '@heidi/runtime/shell';

/** Which copy affordance failed. Both aggregate under one RUM `feature`. */
export type CopySurface = 'note' | 'section';

/**
 * Why this copy produced no clipboard content. `write_failed` is a clipboard
 * write that rejected; `nothing_to_copy` is a copy that never had anything to
 * write. Kept as separate values rather than one bucket because they need
 * different responses — a write failure is a defect, an empty copy is a user
 * acting on a note with no content — and because folding them together would
 * put empty copies in the numerator of the copy-reliability SLI, understating
 * how reliably the clipboard actually works.
 */
export type CopyReportOutcome = 'write_failed' | 'nothing_to_copy';

type CopyFailureContext = {
  surface: CopySurface;
  /**
   * Which editor the copy ran against. Required: a failure that can't be
   * attributed to an editor can't be subtracted from that editor's
   * `session_copied` count, which is the whole point of reporting it.
   */
  editorType: NoteEditorType;
  /** Whether a Tiptap editor instance backed the copy path. */
  hasEditor?: boolean;
  /** The UI surface that initiated the copy, when the caller tracks one. */
  copySource?: string;
};

/** Shared so both surfaces land on one facet — a drifted value splits the rate. */
const COPY_FAILURE_FEATURE = 'session_copy';

/** A coalesced native write never reached the clipboard and is a cancellation, not a failure. */
export const isSupersededCopyError = (error: unknown): error is RtfClipboardError =>
  error instanceof RtfClipboardError && error.code === 'CLIPBOARD_SUPERSEDED';

/**
 * `addError` ships the error's message and stack, and a copy failure is thrown
 * over note content. Every error is reduced to its name, DOMException included:
 * its messages are browser-fixed today, but a name-only rule holds that
 * boundary without depending on that staying true.
 */
const toReportable = (error: unknown): string =>
  `copy failed: ${describeClipboardError(error).name}`;

/**
 * Guarded separately from the report itself: the session id is read off the
 * router singleton, and a copy failure is worth reporting without it. Folding
 * this into the reporting `try` would let an unreadable router swallow the
 * whole error instead of just its correlation key.
 */
const currentSessionId = (): string | undefined => {
  try {
    return getCurrentSessionId();
  } catch {
    return undefined;
  }
};

/**
 * Copy is the terminal step of the scribe workflow, so a failure the user only
 * sees as a toast is otherwise invisible in prod. The `session_copied` success
 * event is the denominator for a failure rate.
 *
 * `session_id` and `editor_type` are emitted under those exact snake_case keys
 * — not this file's camelCase house style — because `session_copied` reaches
 * Datadog as an action carrying props of the same names. Matching them is what
 * lets one query divide these errors by those actions and get a real failure
 * rate per editor; a renamed key here silently makes the ratio unqueryable.
 *
 * Context must stay low-cardinality and free of note content — a copy failure
 * is PHI-adjacent by definition. `session_id` is a Heidi UUID, not PHI.
 * Guarded: reporting must never change whether the caller's copy path resolves.
 */
const report = (
  message: string,
  outcome: CopyReportOutcome,
  context: CopyFailureContext
): void => {
  try {
    const { editorType, ...rest } = context;
    const sessionId = currentSessionId();
    datadogRum.addError(message, {
      feature: COPY_FAILURE_FEATURE,
      ...rest,
      outcome,
      editor_type: editorType,
      ...(sessionId != null && { session_id: sessionId }),
    });
  } catch {
    // The caller's own error handling stays the user-facing contract.
  }
};

export const reportCopyFailure = (error: unknown, context: CopyFailureContext): void => {
  report(toReportable(error), 'write_failed', context);
};

/**
 * A copy the user asked for that had nothing to write — no editor and no
 * stored content. It returns silently and shows no toast, so without this it
 * is invisible everywhere: the clinician sees a Copy button do nothing and we
 * have no record it was ever pressed.
 *
 * Deliberately NOT `session_copied`: nothing reached the clipboard, so counting
 * it as a copy is the same defect this ticket exists to fix. It is reported as
 * its own `outcome` so it can be read on its own and excluded from the
 * reliability ratio.
 */
export const reportEmptyCopy = (context: CopyFailureContext): void => {
  report('copy skipped: nothing to copy', 'nothing_to_copy', context);
};
